import React, { useState } from 'react';
import styled from 'styled-components';
import { Plus, Edit3, Trash2, RefreshCw, MapPin } from 'lucide-react';
import { backofficeApi, formatNumber, extractErrorMessage } from '@kardume/services';
import { useNotification } from '@kardume/hooks';
import type { LocalityDto, LocalityRequestDto } from '@kardume/types';
import {
  PageHeader,
  StatCard,
  Table,
  TableContainer,
  Badge,
  Button,
  ActionButton,
  SearchInput,
  Select,
  TextInput,
  Switch,
  Modal,
  Pagination,
} from '@kardume/ui';
import { useAsync } from '../utils/useAsync';
import { useDebouncedValue } from '../utils/useDebouncedValue';
import DataState from '../components/DataState';

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
  margin-bottom: 1.25rem;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const Toolbar = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-bottom: 1rem;
  flex-wrap: wrap;
  align-items: center;

  > *:first-child {
    flex: 1;
    min-width: 240px;
  }
`;

const RowActions = styled.div`
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;
`;

const NameCell = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;

  strong {
    font-size: 0.9rem;
    color: ${(p) => p.theme.textPrimary};
  }
`;

const CoordinatesRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem;
  margin-top: 0.75rem;
`;

const FieldHint = styled.p`
  margin: 0.45rem 0 0;
  font-size: 0.78rem;
  line-height: 1.45;
  color: ${(p) => p.theme.textMuted};
`;

const ToggleField = styled.label`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  margin-top: 0.85rem;
  padding: 0.7rem 0.85rem;
  border: 1px solid ${(p) => p.theme.border};
  border-radius: ${(p) => p.theme.radius};
  background: ${(p) => p.theme.surfaceAlt};
  font-size: 0.85rem;
  font-weight: 600;
  color: ${(p) => p.theme.textSecondary};
  cursor: pointer;
`;

const ConfirmText = styled.p`
  margin: 0;
  font-size: 0.9rem;
  line-height: 1.5;
  color: ${(p) => p.theme.textSecondary};

  strong {
    color: ${(p) => p.theme.textPrimary};
  }
`;

const STATUS_FILTER = [
  { value: '', label: 'Todas as situações' },
  { value: 'active', label: 'Ativas' },
  { value: 'inactive', label: 'Inativas' },
];

/** Brazilian states (UF) for the locality form. */
const UF_OPTIONS = [
  'AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MT', 'MS', 'MG',
  'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN', 'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO',
].map((uf) => ({ value: uf, label: uf }));

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

const EMPTY_FORM: LocalityRequestDto = { name: '', state: 'PR', active: true };

/**
 * As coordenadas vivem fora do `form` porque o campo é texto livre e o payload
 * é numérico: guardar o que foi digitado permite apagar o campo, corrigir um
 * sinal ou colar do mapa sem que um estado intermediário vire `NaN`.
 */
const EMPTY_COORDINATES = { latitude: '', longitude: '' };

/** `''` = "não mexi nisso" (o backend preserva o que já estava gravado). */
function parseCoordinate(value: string): number | undefined {
  const trimmed = value.trim().replace(',', '.');
  if (!trimmed) return undefined;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : Number.NaN;
}

const LocalitiesPage: React.FC = () => {
  const { showNotification } = useNotification();
  const [search, setSearch] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [page, setPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(20);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<LocalityRequestDto>(EMPTY_FORM);
  const [coordinates, setCoordinates] = useState(EMPTY_COORDINATES);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [deleting, setDeleting] = useState<LocalityDto | null>(null);
  const [removing, setRemoving] = useState<boolean>(false);

  const debouncedSearch = useDebouncedValue(search.trim());

  const { data, loading, error, reload } = useAsync(
    () =>
      backofficeApi.listLocalities({
        page,
        size: pageSize,
        query: debouncedSearch || undefined,
        active: statusFilter === '' ? undefined : statusFilter === 'active',
        sort: 'name,asc',
      }),
    [page, pageSize, debouncedSearch, statusFilter]
  );

  const localities = data?.content ?? [];
  const totalItems = data?.totalElements ?? 0;
  const totalPages = data?.totalPages ?? 0;

  const resetToFirstPage = (): void => setPage(0);

  const openCreate = (): void => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setCoordinates(EMPTY_COORDINATES);
    setIsOpen(true);
  };

  const openEdit = (locality: LocalityDto): void => {
    setEditingId(locality.id);
    setForm({ name: locality.name, state: locality.state, active: locality.active });
    setCoordinates({
      latitude: locality.latitude == null ? '' : String(locality.latitude),
      longitude: locality.longitude == null ? '' : String(locality.longitude),
    });
    setIsOpen(true);
  };

  const update = <K extends keyof LocalityRequestDto>(key: K, value: LocalityRequestDto[K]): void => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const submit = async (): Promise<void> => {
    const latitude = parseCoordinate(coordinates.latitude);
    const longitude = parseCoordinate(coordinates.longitude);

    if (Number.isNaN(latitude) || Number.isNaN(longitude)) {
      showNotification('Latitude e longitude devem ser números (ex.: -24.7133).', 'error');
      return;
    }
    // O par é exigido pelo backend, mas avisar aqui evita a ida e volta: sem as
    // duas, a cidade nunca é encontrada pelo "usar minha localização".
    if ((latitude === undefined) !== (longitude === undefined)) {
      showNotification('Informe latitude e longitude juntas.', 'error');
      return;
    }

    setSubmitting(true);
    try {
      const payload: LocalityRequestDto = { ...form, latitude, longitude };
      if (editingId) {
        await backofficeApi.updateLocality(editingId, payload);
        showNotification('Localidade atualizada com sucesso.', 'success');
      } else {
        await backofficeApi.createLocality(payload);
        showNotification('Localidade criada com sucesso.', 'success');
      }
      setIsOpen(false);
      reload();
    } catch (err) {
      showNotification(extractErrorMessage(err, 'Não foi possível salvar a localidade.'), 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const toggleActive = async (locality: LocalityDto): Promise<void> => {
    try {
      // Sem latitude/longitude no payload de propósito: o backend só sobrescreve
      // o que recebe, então ligar/desligar a cidade não mexe no ponto de referência.
      await backofficeApi.updateLocality(locality.id, {
        name: locality.name,
        state: locality.state,
        active: !locality.active,
      });
      showNotification(
        locality.active ? 'Localidade desativada.' : 'Localidade ativada.',
        'success'
      );
      reload();
    } catch (err) {
      showNotification(extractErrorMessage(err, 'Não foi possível alterar a situação.'), 'error');
    }
  };

  const confirmRemove = async (): Promise<void> => {
    if (!deleting) return;
    setRemoving(true);
    try {
      await backofficeApi.deleteLocality(deleting.id);
      showNotification('Localidade excluída.', 'success');
      setDeleting(null);
      reload();
    } catch (err) {
      showNotification(extractErrorMessage(err, 'Não foi possível excluir a localidade.'), 'error');
    } finally {
      setRemoving(false);
    }
  };

  return (
    <>
      <PageHeader
        title="Localidades"
        subtitle="Cidades onde o Kardume Urbano opera. Somente cidades ativas aparecem na busca do site e do app."
        actions={
          <Button variant="primary" onClick={openCreate}>
            <Plus size={16} /> Nova localidade
          </Button>
        }
      />

      <StatsGrid>
        <StatCard label="Total (respeita o filtro)" value={formatNumber(totalItems)} />
        <StatCard label="Ativas (nesta página)" value={formatNumber(localities.filter((l) => l.active).length)} />
        <StatCard label="Inativas (nesta página)" value={formatNumber(localities.filter((l) => !l.active).length)} />
      </StatsGrid>

      <Toolbar>
        <SearchInput
          placeholder="Buscar por cidade ou UF..."
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            resetToFirstPage();
          }}
        />
        <Select
          options={STATUS_FILTER}
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value);
            resetToFirstPage();
          }}
        />
        <ActionButton title="Atualizar" onClick={reload}>
          <RefreshCw size={16} />
        </ActionButton>
      </Toolbar>

      <DataState
        loading={loading}
        error={error}
        isEmpty={localities.length === 0}
        emptyTitle="Nenhuma localidade encontrada"
        emptyDescription="Cadastre a primeira cidade para habilitar a busca por região."
        onRetry={reload}
      >
        <TableContainer>
          <Table>
            <thead>
              <tr>
                <th>Cidade</th>
                <th style={{ width: '1%', whiteSpace: 'nowrap' }}>UF</th>
                <th style={{ width: '1%', whiteSpace: 'nowrap', textAlign: 'right' }}>Situação</th>
                <th style={{ width: '1%', whiteSpace: 'nowrap', textAlign: 'right' }}>Ações</th>
              </tr>
            </thead>
            <tbody>
              {localities.map((locality) => (
                <tr key={locality.id}>
                  <td>
                    <NameCell>
                      <MapPin size={14} />
                      <strong>{locality.name}</strong>
                    </NameCell>
                  </td>
                  <td style={{ whiteSpace: 'nowrap' }}>{locality.state}</td>
                  <td style={{ textAlign: 'right' }}>
                    <Badge tone={locality.active ? 'success' : 'secondary'}>
                      {locality.active ? 'Ativa' : 'Inativa'}
                    </Badge>
                  </td>
                  <td>
                    <RowActions>
                      <Switch
                        checked={locality.active}
                        onChange={() => toggleActive(locality)}
                        aria-label={locality.active ? 'Desativar localidade' : 'Ativar localidade'}
                      />
                      <ActionButton aria-label="Editar localidade" title="Editar" onClick={() => openEdit(locality)}>
                        <Edit3 size={16} />
                      </ActionButton>
                      <ActionButton
                        aria-label="Excluir localidade"
                        title="Excluir"
                        color="#DC2626"
                        onClick={() => setDeleting(locality)}
                      >
                        <Trash2 size={16} />
                      </ActionButton>
                    </RowActions>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </TableContainer>
      </DataState>

      {totalPages > 1 && (
        <Pagination
          currentPage={page + 1}
          totalPages={totalPages}
          totalItems={totalItems}
          pageSize={pageSize}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
          disabled={loading}
          onPageChange={(next) => setPage(next - 1)}
          onPageSizeChange={(size) => {
            setPageSize(size);
            resetToFirstPage();
          }}
        />
      )}

      <Modal
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        title={editingId ? 'Editar localidade' : 'Nova localidade'}
        width="420px"
        footer={
          <>
            <Button variant="ghost" onClick={() => setIsOpen(false)} disabled={submitting}>
              Cancelar
            </Button>
            <Button variant="primary" onClick={submit} isLoading={submitting}>
              Salvar
            </Button>
          </>
        }
      >
        <TextInput
          label="Cidade"
          value={form.name}
          onChange={(e) => update('name', e.target.value)}
          placeholder="Ex.: Toledo"
        />
        <Select
          label="UF"
          options={UF_OPTIONS}
          value={form.state}
          onChange={(e) => update('state', e.target.value)}
          containerStyle={{ marginTop: '0.75rem' }}
        />
        <CoordinatesRow>
          <TextInput
            label="Latitude"
            value={coordinates.latitude}
            onChange={(e) => setCoordinates((prev) => ({ ...prev, latitude: e.target.value }))}
            placeholder="Ex.: -24.7133"
          />
          <TextInput
            label="Longitude"
            value={coordinates.longitude}
            onChange={(e) => setCoordinates((prev) => ({ ...prev, longitude: e.target.value }))}
            placeholder="Ex.: -53.7433"
          />
        </CoordinatesRow>
        <FieldHint>
          Ponto de referência da cidade (o centro serve). É o que permite ao site e ao app
          descobrirem a cidade pelo GPS do usuário - sem ele, a cidade só aparece para quem
          a escolhe na lista.
        </FieldHint>

        <ToggleField>
          <Switch checked={form.active} onChange={(v) => update('active', v)} />
          Cidade ativa (visível na busca do site/app)
        </ToggleField>
      </Modal>

      <Modal
        isOpen={deleting !== null}
        onClose={() => setDeleting(null)}
        title="Excluir localidade"
        width="420px"
        footer={
          <>
            <Button variant="ghost" onClick={() => setDeleting(null)} disabled={removing}>
              Cancelar
            </Button>
            <Button variant="danger" onClick={confirmRemove} isLoading={removing}>
              Excluir
            </Button>
          </>
        }
      >
        <ConfirmText>
          Tem certeza que deseja excluir <strong>{deleting ? `${deleting.name}, ${deleting.state}` : ''}</strong>?
          A cidade deixará de aparecer na busca do site e do app.
        </ConfirmText>
      </Modal>
    </>
  );
};

export default LocalitiesPage;
